import {LegalEntityTypeSummary} from "../../mappers";
import {
    calculateOutOfEstateLifeInsuranceTotalValue,
    getOutOfEstateTotalValueForBankingAccount,
    getOutOfEstateTotalValueForHeldAwayAccount,
    getOutOfEstateTotalValueForInvestmentProgram,
    getOutOfEstateTotalValueForPartiallyOwnedInvestmentAccount
} from "../../AssetSummary/common/AssetSummaryCalculator";
import {AssetsSummary} from "../../models/Assets";
import {LegalEntityOwnership} from "../../models/Ownership";

export const filterAssetsForOutOfEstateReport = (assets: AssetsSummary): AssetsSummary => {
    return {
        ...assets,
        accounts: {
            ...assets.accounts,
            data: assets.accounts.data.map(filterHiddenLegalEntityOwnerships)
        },
        personalAssets: {
            ...assets.personalAssets,
            data: assets.personalAssets.data.map(filterHiddenLegalEntityOwnerships)
        },
        lifeInsurances: {
            ...assets.lifeInsurances,
            data: assets.lifeInsurances.data.map(filterHiddenLegalEntityOwnerships)
        },
        personalLiabilities: assets.personalLiabilities.map(filterHiddenLegalEntityOwnerships),
        investmentProgram: assets.investmentProgram
            ? {
                ...assets.investmentProgram,
                legalAgreements: assets.investmentProgram.legalAgreements.map(filterHiddenLegalEntityOwnerships),
                heldAwayAccounts: assets.investmentProgram.heldAwayAccounts.map(filterHiddenLegalEntityOwnerships)
            }
            : assets.investmentProgram,
        partiallyOwnedLegalAgreements: assets.partiallyOwnedLegalAgreements.map(filterHiddenLegalEntityOwnerships),
        bankingAccounts: assets.bankingAccounts.map(filterHiddenLegalEntityOwnerships)
    };
};

export const calculateVisibleOutOfEstateReportTotalValue = (
    assets: AssetsSummary,
    useDeathBenefitForLifeInsurance = false
): number => {
    const reportVisibleAssets = filterAssetsForOutOfEstateReport(assets);

    const personalAssetsTotal = sumVisibleOutOfEstateValues(
        assets.personalAssets.data,
        (asset) => asset.presentValue,
        (asset) => asset.presentValue - asset.inEstateValue
    );

    const standaloneAccountsTotal = sumVisibleOutOfEstateValues(
        assets.accounts.data,
        (account) => account.holdings.totalMarketValue ?? 0,
        (account) => (account.holdings.totalMarketValue ?? 0) - account.inEstateValue
    );

    const lifeInsuranceTotal = calculateOutOfEstateLifeInsuranceTotalValue(
        reportVisibleAssets.lifeInsurances.data,
        useDeathBenefitForLifeInsurance
    );

    const investmentProgramTotal = reportVisibleAssets.investmentProgram
        ? getOutOfEstateTotalValueForInvestmentProgram(reportVisibleAssets.investmentProgram.legalAgreements)
        : 0;

    const heldAwayAccountTotal = reportVisibleAssets.investmentProgram
        ? getOutOfEstateTotalValueForHeldAwayAccount(reportVisibleAssets.investmentProgram.heldAwayAccounts)
        : 0;

    const bankingAccountTotal = getOutOfEstateTotalValueForBankingAccount(reportVisibleAssets.bankingAccounts);

    const partiallyOwnedTotal = getOutOfEstateTotalValueForPartiallyOwnedInvestmentAccount(
        reportVisibleAssets.partiallyOwnedLegalAgreements
    );

    const personalLiabilityTotal = sumVisibleOutOfEstateValues(
        assets.personalLiabilities,
        (liability) => liability.loanBalanceEstateValue.totalValue || liability.loanBalance,
        (liability) => liability.loanBalanceEstateValue.outOfEstateValue
    );

    return personalAssetsTotal
        + standaloneAccountsTotal
        + heldAwayAccountTotal
        + bankingAccountTotal
        + partiallyOwnedTotal
        + lifeInsuranceTotal
        + investmentProgramTotal
        - personalLiabilityTotal;
};

export const filterEmptyLegalEntityTypeSummaries = (legalEntityTypeSummaries: LegalEntityTypeSummary[]) => {
    return legalEntityTypeSummaries
        .map((summary) => ({
            ...summary,
            entities: summary.entities.filter((entity) => entity.items.length > 0)
        }))
        .filter((summary) => summary.entities.length > 0);
};

const filterHiddenLegalEntityOwnerships = <T extends { legalEntityOwnerships: LegalEntityOwnership[] }>(asset: T): T => {
    return {
        ...asset,
        legalEntityOwnerships: asset.legalEntityOwnerships.filter((ownership) => ownership.showWithinOutOfEstateReport)
    };
};

const sumVisibleOutOfEstateValues = <T extends { legalEntityOwnerships: LegalEntityOwnership[] }>(
    items: T[],
    getTotalValue: (item: T) => number,
    getOutOfEstateValue: (item: T) => number
) => {
    return items.reduce((previousValue, item) => {
        const totalValue = getTotalValue(item);
        const outOfEstateValue = getOutOfEstateValue(item);

        return previousValue + removeHiddenLegalEntityOwnershipValue(outOfEstateValue, totalValue, item.legalEntityOwnerships);
    }, 0);
};

const removeHiddenLegalEntityOwnershipValue = (
    currentOutOfEstateValue: number,
    totalValue: number,
    legalEntityOwnerships: LegalEntityOwnership[]
) => {
    return Math.max(0, currentOutOfEstateValue - calculateHiddenLegalEntityOwnershipValue(totalValue, legalEntityOwnerships));
};

const calculateHiddenLegalEntityOwnershipValue = (totalValue: number, legalEntityOwnerships: LegalEntityOwnership[]) => {
    const hiddenLegalEntityPercentage = legalEntityOwnerships
        .filter((ownership) => !ownership.showWithinOutOfEstateReport)
        .reduce((previousValue, currentValue) => previousValue + currentValue.percentage, 0);

    return (hiddenLegalEntityPercentage * totalValue) / 100;
};